import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLogin from './AdminLogin';
import PawnDashboard from './PawnDashboard';
import PawnErrorBoundary from './PawnErrorBoundary';
import config from '../config';
import '../styles/repair-app.css';
import '../styles/pawn-app.css';

const DEFAULT_TITLE = 'A2zTak Admin - Easy Finance Management System';
const DEFAULT_MANIFEST = `${process.env.PUBLIC_URL || ''}/manifest.json`;
const PAWN_MANIFEST = `${process.env.PUBLIC_URL || ''}/pawn-manifest.json`;
function PawnCounterApp() {
  const { user, login, logout, loading } = useAuth();
  const [overdueCount, setOverdueCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [installPrompt, setInstallPrompt] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${config.API_URL}/pawn/stats`);
      setOverdueCount(res.data?.overdue || 0);
    } catch {
      /* ignore */
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshTick((t) => t + 1);
    await loadStats();
    setTimeout(() => setRefreshing(false), 600);
  }, [loadStats]);

  useEffect(() => {
    document.title = 'A2ZTAK Pawn';
    const manifest = document.querySelector('link[rel="manifest"]');
    const prevManifest = manifest?.getAttribute('href') || DEFAULT_MANIFEST;
    if (manifest) manifest.setAttribute('href', PAWN_MANIFEST);

    let appleCap = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    const createdCap = !appleCap;
    if (!appleCap) {
      appleCap = document.createElement('meta');
      appleCap.name = 'apple-mobile-web-app-capable';
      document.head.appendChild(appleCap);
    }
    appleCap.content = 'yes';

    let theme = document.querySelector('meta[name="theme-color"]');
    const prevTheme = theme?.getAttribute('content') || '#000000';
    if (theme) theme.setAttribute('content', '#b45309');

    let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    const prevIcon = appleIcon?.getAttribute('href');
    if (appleIcon) appleIcon.setAttribute('href', `${process.env.PUBLIC_URL || ''}/pawn-icon-192.png`);

    let appTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appTitle) {
      appTitle = document.createElement('meta');
      appTitle.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appTitle);
    }
    appTitle.content = 'Pawn';

    return () => {
      document.title = DEFAULT_TITLE;
      if (manifest) manifest.setAttribute('href', prevManifest);
      if (theme) theme.setAttribute('content', prevTheme);
      if (appleIcon && prevIcon) appleIcon.setAttribute('href', prevIcon);
      if (createdCap && appleCap?.parentNode) appleCap.parentNode.removeChild(appleCap);
    };
  }, []);

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    return () => window.removeEventListener('beforeinstallprompt', onBip);
  }, []);

  const runInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  useEffect(() => {
    if (!user) return undefined;
    loadStats();
    const t = setInterval(loadStats, 45000);
    return () => clearInterval(t);
  }, [user, loadStats]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center pawn-app-shell">
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pawn-app-shell">
        <header className="pawn-app-topbar">
          <div className="pawn-app-topbar-brand">
            <div className="pawn-app-logo"><i className="bi bi-gem" /></div>
            <div>
              <div className="pawn-app-topbar-title">A2ZTAK Pawn</div>
              <div className="pawn-app-topbar-sub">Counter login</div>
            </div>
          </div>
        </header>
        <div className="repair-login-wrap">
          <div className="repair-login-card">
            <AdminLogin onLogin={login} />
          </div>
        </div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <div className="pawn-app-shell">
      <header className="pawn-app-topbar">
        <div className="pawn-app-topbar-brand">
          <div className="pawn-app-logo"><i className="bi bi-gem" /></div>
          <div>
            <div className="pawn-app-topbar-title">A2ZTAK Pawn</div>
            <div className="pawn-app-topbar-sub">{today} · Main branch</div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {installPrompt && (
            <button type="button" className="btn btn-sm btn-warning text-dark fw-semibold" onClick={runInstall}>
              Install
            </button>
          )}
          {overdueCount > 0 && (
            <span className="repair-app-ready-pill bg-danger">{overdueCount} overdue</span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-light"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh"
          >
            <i className={`bi bi-arrow-clockwise ${refreshing ? 'spin-refresh' : ''}`} />
          </button>
          <button type="button" className="btn btn-sm btn-light text-warning fw-semibold" onClick={logout}>
            <i className="bi bi-box-arrow-right" />
          </button>
        </div>
      </header>
      <main className="pawn-app-main">
        <PawnErrorBoundary>
          <PawnDashboard mobileApp onStatsChange={loadStats} refreshTick={refreshTick} />
        </PawnErrorBoundary>
      </main>
    </div>
  );
}

export default PawnCounterApp;
